/**
 * GENERATED by /docs-writer from docs/securelink.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/securelink.md`.
 *
 * source-sha: d34e4b1705db8bc09d4f82dccfa16b454ad650eb
 * sources (from the markdown's "Sources" section):
 *   - Android Util 4.55: optisigns-android-utils at 80d6c7c (tag 4.55-test), service/WireGuardService.kt, service/WireGuardVpnService.kt, service/DeviceConfigPoller.kt, data/entity/DeviceInfo.kt
 *   - Pro Util 3.0.10.P27: optisigns-vnc-server branch mdm_yocto_wireguard at 0589791, secureLinkManager.py, secureLinkApi.py, serverEndpoints.py; the tunnel unit and firewall helper in yocto_os
 *   - OptiEdge agent v0.3.10-staff-alpha: optiedge-agent main at 0e1b93a, internal/agent/agent.go, internal/wireguard/, internal/enroll/, internal/updater/
 *   - Laptop enrollment: optisigns-staff-app main at 884503dd, apps/api/src/app/staff/staff-enroll.service.ts, wg-orchestrator.client.ts, region-resolver.ts, staff.service.ts
 *   - Device API: optisigns-mdm-api main at 3051aaa, src/services/securelinkService.ts, src/services/securelinkRegion.ts, src/routes/securelink.ts
 *   - Hubs, failover, addresses, cross-region reach: wg-orchestrator main at ff4c5d4, deploy/failover.sh, internal/ipam/ipam.go, internal/api/peers.go, cmd/wg-orchestrator/main.go
 *   - Which builds run: read-only checks on production devices and deployments, 2026-10-12
 *   - The two 4.55 defects and the kill-switch timings: device tests on lab devices, 2026-10-01
 *   - Who can open Peers: apps/web/src/App.tsx
 */
import { Link } from 'react-router';
import { DocTopology } from '../components/DocTopology';
import { DocsToc } from '../components/DocsToc';

const TOC = [
  { id: 'what-securelink-is-made-of', label: 'What SecureLink is made of' },
  { id: 'which-client-runs-where', label: 'Which client runs where' },
  { id: 'how-android-util-connects', label: 'How Android Util connects' },
  { id: 'how-pro-util-connects', label: 'How Pro Util connects' },
  { id: 'how-the-optiedge-agent-connects', label: 'How the OptiEdge agent connects' },
];

export function SecurelinkDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Network · Concept</p>
        <h1 className="doc-title">SecureLink</h1>
        <p className="doc-lede">
          SecureLink is OptiSigns&rsquo; private WireGuard network. Every paired signage device and every enrolled
          staff laptop holds <strong>one tunnel</strong> to a regional hub and <strong>one address</strong> in the{' '}
          <code>100.64.0.0/10</code> overlay. Staff reach a device&rsquo;s SSH, ADB or VNC port through the hub, and
          devices never reach each other. Three clients put a machine on the network:{' '}
          <strong>Android Util</strong>, <strong>Pro Util</strong> and the <strong>OptiEdge agent</strong>.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-28">28 Oct 2026</time>
        </p>

        <h2 className="doc-h2" id="what-securelink-is-made-of">What SecureLink is made of</h2>
        <p className="doc-p">
          A signage device sits behind a customer&rsquo;s router, where nothing outside can connect to it. With
          SecureLink the device dials out to a hub and keeps that tunnel open. Staff then reach the device through
          the hub.
        </p>
        <p className="doc-p">
          The picture shows the path from a staff laptop to a device. A solid line is traffic inside a tunnel. A
          dashed line is an API asking a hub to create a peer.
        </p>
        <DocTopology
          caption="How a staff laptop reaches a signage device, 12 October 2026"
          groups={[
            { id: 'Hubs', label: 'Hubs', note: 'three regions' },
          ]}
          nodes={[
            { id: 'Laptop', label: 'Staff laptop', note: 'OptiEdge agent' },
            { id: 'DeviceApi', label: 'Device API', note: 'devices join' },
            { id: 'StaffApi', label: 'Staff app API', note: 'enrolls laptops' },
            { id: 'Us', label: 'US hub', note: 'active, standby', group: 'Hubs' },
            { id: 'Eu', label: 'EU hub', note: 'active, standby', group: 'Hubs' },
            { id: 'Apac', label: 'APAC hub', note: 'active, standby', group: 'Hubs' },
            { id: 'Android', label: 'Android player', note: 'Android Util' },
            { id: 'Pro', label: 'ProPlayer', note: 'Pro Util' },
          ]}
          edges={[
            { from: 'Laptop', to: 'Hubs', text: 'UDP 443' },
            { from: 'DeviceApi', to: 'Us', dashed: true },
            { from: 'DeviceApi', to: 'Apac', dashed: true },
            { from: 'StaffApi', to: 'Hubs', dashed: true },
            { from: 'Hubs', to: 'Android', text: 'ADB 5555' },
            { from: 'Hubs', to: 'Pro', text: 'SSH, VNC' },
          ]}
        />
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Part</th>
                <th>Where it runs</th>
                <th>What it does</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Hub</strong></td>
                <td>One active and one standby server in each region: US, EU and APAC</td>
                <td>The WireGuard gateway. It forwards only the traffic its firewall allows.</td>
              </tr>
              <tr>
                <td><strong>wg-orchestrator</strong></td>
                <td>On every hub</td>
                <td>Creates and removes peers, and hands out overlay addresses.</td>
              </tr>
              <tr>
                <td><strong>Device API</strong></td>
                <td>OptiSigns cloud</td>
                <td>
                  The API a signage device calls to join. It checks the device, picks the region and asks that hub
                  for the peer.
                </td>
              </tr>
              <tr>
                <td><strong>Staff app API</strong></td>
                <td>OptiSigns cloud</td>
                <td>
                  Enrolls staff laptops. It checks who you are, picks your subnet from your role and asks the hub
                  for the peer.
                </td>
              </tr>
              <tr>
                <td><strong>This dashboard</strong></td>
                <td>This app</td>
                <td>
                  Shows the network read-only on <Link to="/vpn/peers">Network → Peers</Link>,{' '}
                  <Link to="/vpn/subnets">Network → Subnets</Link> and Connection audit, which is root only.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          <Link to="/docs/vpn-subnets">VPN subnets</Link> lists what each subnet can reach.{' '}
          <Link to="/docs/connection-audit">Connection audit</Link> explains the record of who reached which
          device.
        </p>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            SecureLink is <strong>on by default</strong> for every paired device. The only off switch is an
            account-level kill switch that OptiSigns staff set. The per-device SecureLink flag and the
            account&rsquo;s plan do not decide whether a device connects.
          </p>
        </aside>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            WireGuard runs over <strong>UDP port 443</strong>. On a network that blocks outbound UDP 443 a device
            still provisions over HTTPS. The builds described on this page have no other path, so their tunnel
            never completes a handshake. <Link to="/docs/securelink-tcp-relay">TCP relay</Link> describes the
            fallback over TCP port 443 and the client build that carries it.
          </p>
        </aside>
        <h3 className="doc-h3">Which hub a device uses</h3>
        <ul className="doc-list">
          <li>
            <strong>The device API picks the hub when a device provisions.</strong> It goes by the country of
            the device&rsquo;s public IP, then by the country and time zone on the device&rsquo;s record when the IP
            gives no answer. Devices in Asia, the Middle East, Australia, the Pacific and the Indian Ocean get the
            APAC hub. Every other device gets the US hub, including devices in Europe, because only APAC is
            enabled for placement today.{' '}
            <Link to="/docs/securelink-device-provision">Device provisioning</Link> has the full order.
          </li>
          <li>
            <strong>A device stays on its hub until it provisions again.</strong> A reboot reconnects with the
            saved configuration, so a device that provisioned earlier can still be on the US hub. A move gives the
            device a new address.
          </li>
          <li>
            <strong>Each region has an active hub and a standby.</strong> Both run a check every 30 seconds. When
            the active hub does not answer three health checks, the standby takes over the region&rsquo;s address.
          </li>
          <li>
            <strong>The regions are connected.</strong> A laptop on one hub reaches a device on another. A new or
            moved peer becomes reachable from the other regions within 5 minutes.
          </li>
        </ul>
        <h3 className="doc-h3">How a signage device joins</h3>
        <p className="doc-p">Android Util and Pro Util follow the same steps.</p>
        <ol className="doc-list">
          <li>
            The client reads the device&rsquo;s record. It continues when the device is paired to an account and
            the account&rsquo;s kill switch is off.
          </li>
          <li>
            The client creates a WireGuard key pair on the device, or reuses the one it holds. The private key
            stays on the device.
          </li>
          <li>
            The client sends the device&rsquo;s UUID and its public key to the device API in a signed request.
          </li>
          <li>
            The device API picks the region from the device&rsquo;s public IP and asks that hub&rsquo;s
            wg-orchestrator for a peer in the <code>devices</code> subnet.
          </li>
          <li>
            wg-orchestrator answers with the device&rsquo;s overlay address, the hub&rsquo;s endpoint and the
            hub&rsquo;s public key. A device that asks again in the same region keeps its address.
          </li>
          <li>
            The client brings the tunnel up. Only traffic for <code>100.64.0.0/10</code> uses it, and a keepalive
            goes out every 25 seconds.
          </li>
        </ol>

        <h2 className="doc-h2" id="which-client-runs-where">Which client runs where</h2>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Android Util</th>
                <th>Pro Util</th>
                <th>OptiEdge agent</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Runs on</strong></td>
                <td>Android sticks and players</td>
                <td>ProPlayer, the Linux player</td>
                <td>Staff laptops: macOS, Windows, Linux</td>
              </tr>
              <tr>
                <td><strong>Build described</strong></td>
                <td>4.55</td>
                <td>3.0.10.P27</td>
                <td>v0.3.10-staff-alpha</td>
              </tr>
              <tr>
                <td><strong>WireGuard</strong></td>
                <td>Userspace, on the Android VPN service</td>
                <td>
                  Kernel, interface <code>wg0</code>
                </td>
                <td>Userspace, inside the agent</td>
              </tr>
              <tr>
                <td><strong>Joins through</strong></td>
                <td>The device API</td>
                <td>The device API</td>
                <td>The staff app API, after Google sign-in</td>
              </tr>
              <tr>
                <td><strong>Opens to staff</strong></td>
                <td>ADB on port 5555</td>
                <td>SSH on port 3000, VNC on port 5900</td>
                <td>Nothing. It is the staff side.</td>
              </tr>
              <tr>
                <td><strong>Checks the tunnel by</strong></td>
                <td>Handshake age, every 60 seconds</td>
                <td>Whether its tunnel service runs, every 30 seconds</td>
                <td>Pinging the gateway, every 30 seconds</td>
              </tr>
              <tr>
                <td><strong>Reports to the server</strong></td>
                <td>Connected or disconnected</td>
                <td>Nothing</td>
                <td>Nothing</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The table and the sections below describe the builds named in it. Other builds can differ.
        </p>

        <h2 className="doc-h2" id="how-android-util-connects">How Android Util connects</h2>
        <p className="doc-p">
          Android Util is the system app on OptiSigns Android sticks and players. All six of its build flavors
          carry SecureLink. It follows the six steps a signage device takes to join. These points are specific to
          it.
        </p>
        <ul className="doc-list">
          <li>
            <strong>Three signals make it read the device record.</strong> They are the Player app&rsquo;s
            broadcast, a push from the device API, and its own poll. The poll runs every 5 minutes by default, and
            the server can stretch it to at most 60 minutes. A Player broadcast can switch SecureLink on and never
            off.
          </li>
          <li>
            <strong>It keeps one key.</strong> Android Util creates the key pair once and reuses it for every
            provision.
          </li>
          <li>
            <strong>The tunnel is userspace WireGuard on the Android VPN service.</strong> Android Util is a system
            app, so Android shows no VPN consent prompt.
          </li>
          <li>
            <strong>It opens ADB.</strong> When the tunnel starts, Android Util turns on ADB over TCP port 5555.
            When SecureLink is switched off, it turns network ADB off again.
          </li>
          <li>
            <strong>A reboot reconnects from the saved configuration.</strong> It makes no request to the device
            API.
          </li>
        </ul>
        <h3 className="doc-h3">How it recovers</h3>
        <ol className="doc-list">
          <li>
            Every 60 seconds Android Util reads the age of the last WireGuard handshake. An age over 180 seconds
            is <strong>stale</strong>.
          </li>
          <li>
            On the first and the second stale check it restarts the tunnel on the device. It makes no server
            request.
          </li>
          <li>
            On the third stale check it provisions again with the same key. That is one request, and the address
            stays.
          </li>
          <li>
            On the fourth it pauses recovery for 300 seconds. Later rounds pause for 900, 1,800 and then 3,600
            seconds. The 60-second check keeps running during a pause.
          </li>
          <li>
            A fresh handshake resets the count. The network coming back lifts a pause, at most once every 600
            seconds.
          </li>
        </ol>
        <p className="doc-p">
          A provision request that fails is retried with the same key after 30 seconds, then at doubling intervals
          up to 600 seconds. Android Util reports <strong>connected</strong> or <strong>disconnected</strong> to
          the device API whenever the state changes. A device that never completes a handshake stays{' '}
          <strong>provisioned</strong>.
        </p>
        <h3 className="doc-h3">What goes wrong in 4.55</h3>
        <aside className="doc-note doc-note--warn">
          <span className="doc-note-label">Warning</span>
          <p>
            <strong>A restart that fails leaves the tunnel down.</strong> In step 2 the restart can fail, for
            example when DNS is down and the hub&rsquo;s name does not resolve. Build 4.55 then stops recovering.
            It has no tunnel, makes no further attempt and reports no change. The device stays online without
            SecureLink until someone cycles the account kill switch or the app restarts.
          </p>
        </aside>
        <aside className="doc-note doc-note--warn">
          <span className="doc-note-label">Warning</span>
          <p>
            <strong>A device moved to another environment keeps its old tunnel.</strong> When a paired device is
            pointed at another environment while its tunnel is up, build 4.55 keeps the tunnel of the old
            environment. A reboot does not change that, because it reconnects from the saved configuration. Cycle
            the account kill switch in the new environment to make the device provision there.
          </p>
        </aside>
        <p className="doc-p">
          Cycling the kill switch disconnects every device of that account for a few minutes. On two lab devices
          the tunnel went down 180 seconds after the switch was set, and came back 270 seconds after it was
          cleared.
        </p>

        <h2 className="doc-h2" id="how-pro-util-connects">How Pro Util connects</h2>
        <p className="doc-p">
          Pro Util is the remote agent on ProPlayer, OptiSigns&rsquo; Linux player. It follows the six steps a
          signage device takes to join. These points are specific to it.
        </p>
        <ul className="doc-list">
          <li>
            <strong>It polls every 30 seconds.</strong> It reads the device&rsquo;s record, and tries again 10
            seconds after an error.
          </li>
          <li>
            <strong>It makes its key with the WireGuard tools.</strong> It keeps the key while the tunnel exists.
            Switching SecureLink off deletes the key, and the next start makes a new one.
          </li>
          <li>
            <strong>The tunnel is kernel WireGuard.</strong> Pro Util writes the configuration for interface{' '}
            <code>wg0</code> and starts it as a system service.
          </li>
          <li>
            <strong>The firewall opens two ports, on the tunnel only.</strong> SSH listens on port 3000 and VNC on
            port 5900.
          </li>
          <li>
            <strong>It checks that the tunnel service runs.</strong> It does not read the handshake, so it does
            not notice a tunnel that is up and carries nothing. When the service is not running, Pro Util
            provisions again and restarts it, and retries every 30 seconds.
          </li>
          <li>
            <strong>It reports no status.</strong> The device API records the device as connected as soon as it
            provisions.
          </li>
        </ul>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            Pro Util knows two environments, sandbox and production. Every other player environment counts as
            production. A ProPlayer whose player is set to QA joins the production network.
          </p>
        </aside>

        <h2 className="doc-h2" id="how-the-optiedge-agent-connects">How the OptiEdge agent connects</h2>
        <p className="doc-p">
          The OptiEdge agent is one program for staff laptops on macOS, Windows and Linux. It runs as a system
          service, with a tray icon on macOS and Windows and a local page for sign-in.
        </p>
        <h3 className="doc-h3">How a laptop enrolls</h3>
        <ol className="doc-list">
          <li>
            You sign in with your <code>@optisigns.com</code> Google account on the agent&rsquo;s local page.
          </li>
          <li>
            The agent creates a WireGuard key pair and sends the public key and the laptop&rsquo;s hostname to the
            staff app API.
          </li>
          <li>
            The staff app picks your subnet from your highest staff-app role.{' '}
            <Link to="/docs/vpn-subnets">VPN subnets</Link> lists which role lands where.
          </li>
          <li>
            The staff app picks the region. The first laptop you enroll sets it from the laptop&rsquo;s public
            address, with US as the fallback. Later laptops reuse it.
          </li>
          <li>
            The staff app asks that hub&rsquo;s wg-orchestrator for the peer, and returns the address, the
            hub&rsquo;s endpoint and the hub&rsquo;s public key.
          </li>
          <li>The agent saves them and brings the tunnel up.</li>
        </ol>
        <h3 className="doc-h3">What the tunnel carries</h3>
        <ul className="doc-list">
          <li>
            <strong>Split tunnel by default.</strong> Only traffic for the overlay uses the tunnel. The agent
            skips the addresses kept free for Tailscale, which <Link to="/docs/vpn-subnets">VPN subnets</Link>{' '}
            lists.
          </li>
          <li>
            <strong>Full tunnel on macOS only.</strong> It sends all of the laptop&rsquo;s traffic through the
            hub. Linux and Windows refuse the option.
          </li>
          <li>
            <strong>Production and sandbox at once.</strong> One agent holds a session for each, with its own key.
          </li>
        </ul>
        <h3 className="doc-h3">How it stays connected and leaves</h3>
        <ul className="doc-list">
          <li>
            <strong>It pings the gateway every 30 seconds.</strong> After the laptop wakes or changes network, the
            agent rebinds the tunnel&rsquo;s socket. After a sustained failure it restarts the tunnel.
          </li>
          <li>
            <strong>It keeps its key across restarts and reboots.</strong> A new key appears only after you
            disconnect.
          </li>
          <li>
            <strong>Disconnect removes the peer.</strong> The agent takes the tunnel down, asks the staff app to
            delete the peer and wipes its saved state.
          </li>
          <li>
            <strong>Disabling a person removes their peers.</strong> A root user disables the person in the staff
            app, and the staff app deletes that person&rsquo;s peers.
          </li>
          <li>
            <strong>It updates itself on macOS.</strong> It checks for a new release every 6 hours. Windows and
            Linux need a manual install.
          </li>
        </ul>

        <aside className="doc-next">
          <div>
            <h2 className="doc-next-title">Checking one peer?</h2>
            <p>
              Network → Peers lists every device and laptop peer with the time since its last handshake. Every
              role except new member can open it.
            </p>
          </div>
          <Link className="btn" to="/vpn/peers">
            Open Peers
          </Link>
        </aside>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
